/**
 * URL-locale → Intl-locale mapping.
 *
 * Shared across price, date, and number formatting — keep this module free of
 * domain-specific concerns (currency positioning, DOB rules, etc.).
 */

/**
 * Maps URL locale codes (e.g. 'us-en') to BCP 47 Intl locale strings (e.g. 'en-US').
 */
const LOCALE_MAP: Record<string, string> = {
  // Americas
  'us-en': 'en-US',
  'us-es': 'es-US',
  'ca-en': 'en-CA',
  'ca-fr': 'fr-CA',
  'mx-es': 'es-MX',
  'br-pt': 'pt-BR',

  // Europe
  'gb-en': 'en-GB',
  'de-de': 'de-DE',
  'de-en': 'en-DE',
  'fr-fr': 'fr-FR',
  'fr-en': 'en-FR',
  'it-it': 'it-IT',
  'it-en': 'en-IT',
  'es-es': 'es-ES',
  'es-en': 'en-ES',
  'nl-en': 'en-NL',
  'be-en': 'en-BE',
  'be-fr': 'fr-BE',
  'at-de': 'de-AT',
  'at-en': 'en-AT',
  'ch-de': 'de-CH',
  'ch-fr': 'fr-CH',
  'ch-it': 'it-CH',
  'ch-en': 'en-CH',
  'se-en': 'en-SE',
  'dk-en': 'en-DK',
  'no-en': 'en-NO',
  'fi-en': 'en-FI',
  'ie-en': 'en-IE',
  'pl-en': 'en-PL',
  'cz-en': 'en-CZ',

  // Asia Pacific
  'jp-ja': 'ja-JP',
  'jp-en': 'en-JP',
  'kr-ko': 'ko-KR',
  'kr-en': 'en-KR',
  'hk-zht': 'zh-Hant-HK',
  'hk-en': 'en-HK',
  'tw-zht': 'zh-Hant-TW',
  'tw-en': 'en-TW',
  'sg-en': 'en-SG',
  'au-en': 'en-AU',
  'nz-en': 'en-NZ',

  // Middle East
  'ae-en': 'en-AE',
  'sa-en': 'en-SA',
  'qa-en': 'en-QA',
  'kw-en': 'en-KW',
  'bh-en': 'en-BH',
};

/**
 * Get Intl locale string from URL locale (e.g., 'us-en' → 'en-US').
 *
 * Example Input: urlLocale='ch-de'
 * Example Output: 'de-CH'
 */
export function getIntlLocale(urlLocale: string | undefined): string {
  if (!urlLocale) return 'en-US';
  const normalized = urlLocale.toLowerCase();
  return LOCALE_MAP[normalized] || 'en-US';
}
